import { Injectable } from '@angular/core';
import { POSType } from '@digilize/shared/definitions/src';
import { BehaviorSubject, catchError, combineLatest, finalize, map, Observable, of, tap } from 'rxjs';

import { CartDto } from '../../infrastructure/dto/products.dto';
import { ProductsService } from '../../infrastructure/products.service';
import { isGiftCardProduct, posSellsGiftCards, sellsPredefinedAmounts } from '../../models/gift-card';
import { Product, ProductPredefinedAmount, roundToCents } from '../../models/product';
import { CartState } from './cart.state';

/** One reserved gift card: the product it came from, the amount it was bought at, and its cart line. */
interface GiftCardLine {
  productId: string;
  cartLineId: string;
  /** The predefined amount this line was reserved at; null for a gift card sold at its own price. */
  amount: number | null;
}

/** A gift card in the cart, as the order summary renders it. */
export interface GiftCardSelection {
  product: Product;
  /** Null when the product is priced normally, in which case its own price is what was paid. */
  amount: number | null;
  quantity: number;
}

/**
 * The gift cards a Prepaid online shop sells alongside its non-recurring subscriptions, which
 * {@link SubscriptionState} handles. Deliberately a state of its own: a subscription is a
 * single-choice selection (switching product releases the previous lines) whereas gift cards are an
 * ordinary basket — several amounts at once, combined with a subscription in the same order.
 *
 * One product carries several predefined amounts (PAUL-3071), so a selection is a (product, amount)
 * pair throughout: two amounts of the same gift card are two rows with counters of their own, and
 * the amount travels to the backend on every add. Free amount entry stays a Cash Register
 * capability, so nothing here ever takes an amount the product does not offer.
 */
@Injectable({
  providedIn: 'root',
})
export class GiftCardState {
  private readonly _products = new BehaviorSubject<Product[]>(null);
  readonly products$ = this._products.asObservable();

  private readonly _lines = new BehaviorSubject<GiftCardLine[]>([]);

  private readonly _loading = new BehaviorSubject<boolean>(false);
  readonly loading$ = this._loading.asObservable();

  /**
   * Gift cards currently in the cart, grouped per product *and* amount, in the order they were first
   * added. Grouping by product alone would collapse two amounts of the same gift card into one row
   * and report the wrong quantity for both — which is also how the backend's own summary groups them.
   */
  readonly selections$: Observable<GiftCardSelection[]> = combineLatest([this._products, this._lines]).pipe(
    map(([products, lines]) => {
      const productsById = new Map((products ?? []).map((product) => [product.id, product]));
      const selections: GiftCardSelection[] = [];
      lines.forEach((line) => {
        const product = productsById.get(line.productId);
        if (!product) {
          return;
        }
        const existing = selections.find(
          (selection) => selection.product.id === product.id && selection.amount === line.amount
        );
        if (existing) {
          existing.quantity += 1;
        } else {
          selections.push({ product, amount: line.amount, quantity: 1 });
        }
      });
      return selections;
    })
  );

  readonly hasSelection$ = this._lines.pipe(map((lines) => lines.length > 0));

  constructor(private readonly productsService: ProductsService, private readonly cartState: CartState) {}

  get hasSelection(): boolean {
    return this._lines.value.length > 0;
  }

  /** Null until {@link loadProducts} has answered — "not loaded yet" is not the same as "none". */
  get products(): Product[] | null {
    return this._products.value;
  }

  /** Synchronous read for click handlers that must not fire a second cart request over a pending one. */
  get loading(): boolean {
    return this._loading.value;
  }

  /**
   * Loads the gift cards configured on this POS from its product list. A POS that does not sell them
   * ends up with an empty list and no request at all, and a failing add-ons call yields an empty list
   * too: gift cards are an addition to the prepaid shop, so losing them must not take the
   * subscription list — and with it the whole checkout — down as well.
   */
  loadProducts(posId: string, posType: POSType): Observable<Product[]> {
    if (!posSellsGiftCards(posType)) {
      this._products.next([]);
      return of([]);
    }
    this._loading.next(true);
    return this.productsService.getAddonsByPosId(posId).pipe(
      map((products) => products.filter(isGiftCardProduct)),
      catchError(() => of([])),
      tap((products) => this._products.next(products)),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Narrows the offer to one gift card at one of its amounts, for a `?product=&amount=` link: the
   * product keeps that amount alone and every other gift card on the POS is dropped. Done to the
   * loaded list rather than to the request, so everything reading this state — the selection rows and
   * the cart preview alike — agrees on what is for sale. Lines already restored from a cart are left
   * as they are: they were bought before the link narrowed anything.
   */
  restrictToAmount(product: Product, amount: ProductPredefinedAmount): void {
    this._products.next([{ ...product, predefinedAmounts: [amount] }]);
  }

  /** How many of this gift card the cart holds at this amount — every amount counts on its own. */
  getQuantity(productId: string, amount: number | null): Observable<number> {
    return this._lines.pipe(
      map((lines) => lines.filter((line) => line.productId === productId && line.amount === amount).length)
    );
  }

  /**
   * Reserves one more of this gift card at `amount`, firing the reservation immediately like the
   * tickets flow. The amount is a required argument rather than something derived here: a gift card
   * sold by predefined amounts has no price of its own, and a reservation that leaves the amount out
   * is priced at `0.00` — or at whatever the cart's first line of the product cost — without failing.
   */
  addOne(product: Product, amount: number | null): Observable<void> {
    // Nothing offers an amountless add for such a product, so this can only be a mistake in a caller;
    // refusing it is the one safe outcome, since going ahead would sell the card for the wrong money.
    if (sellsPredefinedAmounts(product) && amount === null) {
      return of(undefined);
    }
    this._loading.next(true);
    return this.cartState.reserveGiftCard(product.id, amount).pipe(
      tap((cartLineId) => this._lines.next([...this._lines.value, { productId: product.id, cartLineId, amount }])),
      map(() => undefined),
      finalize(() => this._loading.next(false))
    );
  }

  /** Releases the most recently reserved line of this gift card *at this amount*. */
  removeOne(product: Product, amount: number | null): Observable<void> {
    const lines = this._lines.value;
    const index = lines.map((line) => line.productId === product.id && line.amount === amount).lastIndexOf(true);
    if (index === -1) {
      return of(undefined);
    }
    const { cartLineId } = lines[index];
    this._loading.next(true);
    return this.cartState.removeShowlessLine(cartLineId).pipe(
      // Re-read the lines instead of splicing the captured array: another +/- may have resolved while
      // this removal was in flight.
      tap(() => this._lines.next(this._lines.value.filter((line) => line.cartLineId !== cartLineId))),
      map(() => undefined),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Rebuilds the gift-card basket from a persisted cart so a page refresh keeps it. Products must be
   * loaded first; lines whose product is not a gift card on this POS — subscriptions, above all — are
   * left to their own state. Returns true when at least one gift card was restored.
   */
  restoreFromCart(cart: CartDto): boolean {
    const productsById = new Map((this._products.value ?? []).map((product) => [product.id, product]));
    const lines: GiftCardLine[] = [];
    (cart.summary?.lines ?? []).forEach((line) => {
      const product = line.product_id ? productsById.get(line.product_id) : undefined;
      if (!product) {
        return;
      }
      lines.push({
        productId: product.id,
        cartLineId: line.id,
        // Only a product sold by amounts kept one. On any other gift card the line amount is just its
        // catalogue price, and remembering it as a chosen amount would key the row's counter off
        // something the +/- buttons never set, leaving the restored row showing zero.
        amount: sellsPredefinedAmounts(product) ? roundToCents(line.amount) : null,
      });
    });
    this._lines.next(lines);
    return lines.length > 0;
  }

  reset(): void {
    this._products.next(null);
    this._lines.next([]);
    this._loading.next(false);
  }
}
